import React, {Component} from 'react';
import styled from 'styled-components';
import {Link} from 'react-router-dom';

import {create as createAnswer} from '../../lib/answer';
import {withUser} from '../../lib/user';
import Answer from '../Answer';
import AnswerInput from '../AnswerInput';
import Widget from '../Widget';
import Input from '../Input';
import Button from '../Button';
import Icon from '../Icon';
import NoDataWidget from '../NoDataWidget';
import CanEditUser from '../../security/rules/CanEditUser';
import CanEditTemplate from '../../security/rules/CanEditTemplate';
import WidgetHeader from '../WidgetHeader';
import { UserLink } from '../UserLink';
import Notification from '../Notification';
import {getQuestionDisplayLabel, getInventoryQuestionsUsingLot} from '../../screens/FormBuilderScreen/shared';

class ContextWidget extends Component {
  state = {
    sheetId: Object.keys(this.props.sheetsById)[0],
    edit: null
  };

  handleEdit(question, answers) {
    this.setState({
      edit: {
        question, 
        values: answers.map(answer => answer.value),
        isValid: true
      }
    });
  }

  handleChangeAnswers = (values, isValid) => {
    this.setState(state => ({
      edit: {
        ...state.edit,
        values,
        isValid
      }
    }));
  }

  handleSaveAnswers = () => {
    const {onAnswersChanged, sheetsById} = this.props;
    const {sheetId, edit: {question, values}} = this.state;
    const answers = values.map(value => createAnswer(question.id, value));
    const sheet = sheetsById[sheetId];

    this.setState({edit: null});
    onAnswersChanged(sheet, question.id, answers);
  }

  render() {
    const {sheetsById, catalog, template, questions, answers, user, onHide, published} = this.props;
    const {edit, sheetId} = this.state;

    let hideOption;

    if(user.role !== "USER") {
      hideOption = {
        title: 'Hide',
        onClick: () => onHide({
          source: 'Context', 
          widget: {
            id:'context-widget', 
            title: "Sheet Context"
          }
        })
      }
    }

    if (Object.keys(sheetsById).length < 1) {
      return (
        <Widget id="context-widget" title="Sheet Context">
          <NoDataWidget title={"Sheet Context"} id={"context-widget"} options={published && [hideOption]}/>
        </Widget>
      );
    }

    const sheets = Object.keys(sheetsById).map(id => sheetsById[id]);
    const sheet = sheetsById[sheetId];
    const dateCreated = new Date(parseInt(sheet.dates.created, 10)).toLocaleString();
    const dateModified = new Date(parseInt(sheet.dates.lastModified, 10)).toLocaleString();

    return (
      <Widget style={{overflowY: "auto"}} id="context-widget" >
        <WidgetHeader title="Sheet Context" id={"context-widget"} options={[hideOption]}/>
        {sheets.length > 1 && (
          <SheetSelector>
            <Input
              type="select"
              id="sheet-selector"
              label="Select a Sheet"
              value={sheetId}
              onChange={e => this.setState({sheetId: e.target.value})}>
              {sheets.map(sheet => (
                <option key={sheet.id} value={sheet.id}>{sheet.name || 'Unnamed sheet'} by {sheet.user.userName}</option>
              ))}
            </Input>
          </SheetSelector>
        )}
        <Table>
          <tbody>
            <tr>
              <td>Submissions</td>
              <td colSpan={2}>{sheet.rowCount.toLocaleString()}</td>
            </tr>
            <tr>
              <td>Completed</td>
              <td colSpan={2}>{sheet.completed ? "Yes" : "No"}</td>
            </tr>
            <tr>
              <td>Sheet Created by User</td>
              <td colSpan={2}>
                {CanEditUser(user, sheet.user) ? (
                  <UserLink user={sheet.user} />
                ) : sheet.user.userName}
              </td>
            </tr>
            <tr>
              <td>Form</td>
              <td colSpan={2}>
                {CanEditTemplate(user, template) ? (
                  <Link to={`/forms/${template.id}`}>{template.name}</Link>
                ) : template.name}
              </td>
            </tr>
            <tr>
              <td>Date Created</td>
              <td colSpan={2}>{dateCreated}</td>
            </tr>
            <tr>
              <td>Date Modified</td>
              <td colSpan={2}>{dateModified}</td>
            </tr>
            {questions.map((question, i) => {
              const sheetAnswers = answers[i].filter(answer => answer.sheetId === sheet.id);

              return (
                <tr key={question.id}>
                  <td>{getQuestionDisplayLabel(question)}</td>
                  {edit && edit.question.id === question.id ? (
                    <>
                      <td>
                        <AnswerEditor>
                          <AnswerInput
                            invalid={!edit.isValid}
                            question={edit.question}
                            catalog={catalog}
                            values={edit.values}
                            onChange={this.handleChangeAnswers}
                          />
                          {getInventoryQuestionsUsingLot(template, edit.question).length > 0 && (
                            <LotNote info mini>
                              Inventory answers read their lot from this answer. Saving moves their stock to the new lot.
                            </LotNote>
                          )}
                        </AnswerEditor>
                      </td>
                      <ButtonCell>
                        <Button secondary disabled={!edit.isValid} onClick={this.handleSaveAnswers}>Save</Button>
                      </ButtonCell>
                    </>
                  ) : (
                    <>
                      <td style={{whiteSpace: 'normal', wordWrap: 'break-word'}}>
                        <Answer question={question} answers={sheetAnswers} catalog={catalog}/>
                      </td>
                      <ButtonCell>
                        {user.role === 'ADMIN' && (
                          <Button tertiary onClick={() => this.handleEdit(question, sheetAnswers)}>
                            <Icon name="edit"/>
                          </Button>
                        )}
                      </ButtonCell>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Widget>
    );
  }
}

const SheetSelector = styled.div`
  padding: 0 1.875rem 1rem 1.875rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  tr:nth-child(odd) {
    background-color: #f5f5f8;
  }

  td {
    height: 2.5rem;

    :first-child {
      font-weight: 500;
      padding-left: 1.875rem;
    }

    :last-child {
      padding-right: 1.875rem;
    }

    :nth-child(2) {
      text-align: right;
    }
  }
`;

const ButtonCell = styled.td`
  width: 1px; // Will make the cell resize to the exact width of the button.

  button {
    margin-left: 0.5rem;
  }
`;

const AnswerEditor = styled.div``;

const LotNote = styled(Notification)`
  margin-top: 0.5rem;
  text-align: left;
`;

export default withUser(ContextWidget);
